import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  FORM_TEXTAREA_CLASS,
  Modal,
  TABLE_HEADER_CELL_CLASS,
  useModalRequestClose,
} from '@safetyhub/ui';
import {
  LEGAL_OBLIGATION_AGENCY_FIELD_LABEL,
  LEGAL_OBLIGATION_APPLICABILITY_SCOPE_LABEL,
  LEGAL_OBLIGATION_CONDITION_AXIS_LABEL,
  LEGAL_OBLIGATION_DEADLINE_BASIS_LABEL,
  LEGAL_OBLIGATION_EVIDENCE_SOURCE_LABEL,
  LEGAL_OBLIGATION_INTERVAL_ANCHOR_LABEL,
  LEGAL_OBLIGATION_INTERVAL_UNIT_LABEL,
  LEGAL_OBLIGATION_PERIOD_BASIS_LABEL,
  LEGAL_OBLIGATION_RECURRENCE_LABEL,
  LEGAL_OBLIGATION_RESPONSIBLE_PARTY_LABEL,
  LEGAL_POSITIONS,
  LEGAL_POSITION_ORDER,
  OBLIGATION_RECURRENCE_VALUES,
  formatDateTimeISO,
  formatObligationInterval,
  formatObligationRecurrence,
  type AdminLegalObligationConditionView,
  type AdminLegalObligationRow,
} from '@safetyhub/shared';
import { extractApiError } from '../lib/api';
import {
  LEGAL_OBLIGATION_CATALOG_QUERY_KEYS,
  legalObligationCatalogApi,
} from '../lib/legalObligationCatalog';
import {
  CATALOG_MISSING_FIELD_LABEL,
  blocksMaterialization,
  buildUpdateBody,
  formBlockedReason,
  formToRule,
  missingCatalogFields,
  revisionFieldLabel,
  rowToForm,
  type CatalogFormState,
} from '../lib/legalObligationCatalogFields';
import { useToastStore } from '../stores/toast';
import UpcomingAmendmentsPanel from '../components/legal-obligations/UpcomingAmendmentsPanel';
import ObligationTemplateLinksModal from '../components/legal-obligations/ObligationTemplateLinksModal';

/**
 * **법정 의무 카탈로그** — 플랫폼이 한 벌 관리하는 의무 정의를 고치는 화면(사용자 결정 2026-10-29 「화면 먼저」).
 *
 * ## 왜 이 화면이 필요했나
 * 활성 의무 37개가 제목만 있고 주기·기간 기준·기한 기준·담당 주체가 비어 있었다(seed가 채우지 않는다 —
 * 그 값은 조문 검증의 산출물이다 · 모델 docblock). 주기가 비면 실체화가 `RECURRENCE_UNSET`으로 skip되어
 * **어느 현장에도 인스턴스가 생기지 않는다.** 고칠 서버 API(`PATCH`)는 있었고 화면이 없었다.
 *
 * ## 이 화면이 «하지 않는» 것
 * - **적용 조건을 고치지 않는다** — 서버에 그 쓰기 입구가 없다(카탈로그 서비스 docblock). 조건은 보기 전용으로
 *   싣고, 열려면 이력 스냅샷·개정 축 판정과 함께 서버에서 먼저 정해야 한다.
 * - `key`·`lastVerifiedAt`·`displayOrder`·`smsEscalation`을 고치지 않는다 — PATCH DTO에 칸이 없다(그 넷을
 *   여는 판정은 DTO docblock의 ⓐⓑⓒ다). 값은 보여 준다.
 * - 개정 «되돌리기»를 주지 않는다 — 서버 docblock이 그 UX를 정하지 않았다.
 *
 * 저장은 서버 `LegalObligationCatalogService.updateObligation()`을 지나 **이력 1행 + version +1**이 된다
 * (달라진 칸이 없으면 no-op). 그 사실을 편집 모달에 적는다.
 */
export default function LegalObligationCatalog() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AdminLegalObligationRow | null>(null);
  const [historyOf, setHistoryOf] = useState<AdminLegalObligationRow | null>(null);
  // L24 — 의무 ↔ 양식 링크 저작(카탈로그 개정과 다른 축이라 이력·version을 건드리지 않는다)
  const [templatesOf, setTemplatesOf] = useState<AdminLegalObligationRow | null>(null);
  const [onlyMissing, setOnlyMissing] = useState(false);

  const list = useQuery({
    queryKey: LEGAL_OBLIGATION_CATALOG_QUERY_KEYS.list,
    queryFn: () => legalObligationCatalogApi.list(),
  });

  const rows = useMemo(() => list.data ?? [], [list.data]);
  const activeRows = rows.filter((r) => r.isActive);
  const blockedCount = activeRows.filter(blocksMaterialization).length;
  const missingCount = activeRows.filter((r) => missingCatalogFields(r).length > 0).length;
  const visibleRows = onlyMissing ? rows.filter((r) => missingCatalogFields(r).length > 0) : rows;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">법정 의무 카탈로그</h1>
        <p className="mt-1 text-sm text-gray-500">
          모든 현장에 공통으로 적용되는 법정 의무의 정의입니다. 주기가 비어 있는 의무는 현장에 이행
          항목이 만들어지지 않습니다.
        </p>
      </div>

      {/* L25 — 시행 예정 개정(수신 범위: 운영사만 · 사용자 결정 2026-10-28). 목록 조회와 독립이다 */}
      <UpcomingAmendmentsPanel />

      {list.isLoading ? (
        <div className="py-12 text-center text-gray-500">불러오는 중...</div>
      ) : list.error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
          카탈로그를 불러오지 못했습니다. {extractApiError(list.error).message}
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
            <span data-testid="catalog-summary" className="text-gray-700">
              활성 {activeRows.length}개 중 미기재 칸 있음 <strong>{missingCount}</strong>개 · 주기가
              비어 이행 항목이 안 만들어지는 의무 <strong className="text-rose-700">{blockedCount}</strong>개
            </span>
            <label className="ml-auto inline-flex items-center gap-2 text-gray-700">
              <input
                type="checkbox"
                checked={onlyMissing}
                onChange={(e) => setOnlyMissing(e.target.checked)}
              />
              미기재 칸 있는 것만
            </label>
          </div>

          {visibleRows.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              {onlyMissing ? '미기재 칸이 있는 의무가 없습니다.' : '카탈로그 행이 없습니다.'}
            </div>
          ) : (
            <Card padding="none" className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className={TABLE_HEADER_CELL_CLASS}>문서명</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>주기</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>기간 기준</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>기한 기준</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>담당 주체</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>대행 분야</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>활성</th>
                    <th className={TABLE_HEADER_CELL_CLASS}>상태</th>
                    <th className={`${TABLE_HEADER_CELL_CLASS} text-right`}>버전</th>
                    <th className={`${TABLE_HEADER_CELL_CLASS} text-right`}>관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {visibleRows.map((row) => (
                    <CatalogRow
                      key={row.id}
                      row={row}
                      onEdit={() => setEditing(row)}
                      onHistory={() => setHistoryOf(row)}
                      onTemplates={() => setTemplatesOf(row)}
                    />
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}

      {editing && (
        <CatalogEditModal
          // remount로 폼 초기값을 그 행에서 다시 읽는다(선례 `Announcements`의 같은 이유)
          key={editing.id}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: LEGAL_OBLIGATION_CATALOG_QUERY_KEYS.list });
            queryClient.invalidateQueries({
              queryKey: LEGAL_OBLIGATION_CATALOG_QUERY_KEYS.history(editing.id),
            });
          }}
        />
      )}

      {historyOf && <CatalogHistoryModal row={historyOf} onClose={() => setHistoryOf(null)} />}

      {templatesOf && (
        <ObligationTemplateLinksModal obligation={templatesOf} onClose={() => setTemplatesOf(null)} />
      )}
    </div>
  );
}

/**
 * 비어 있으면 「미기재」를 흐리게 — 빈 칸과 값 「미정」(`UNDETERMINED`)을 같은 말로 읽지 않게 한다.
 * (종전 `emptyLabel` 「미검토」는 대행 분야 칸이 NOT NULL이 되며 걷혔다 — 2026-11-03)
 */
function ValueCell({ label }: { label: string | null }) {
  return (
    <td className="px-4 py-3 text-sm whitespace-nowrap">
      {label === null ? <span className="text-ink-400">미기재</span> : label}
    </td>
  );
}

function CatalogRow({
  row,
  onEdit,
  onHistory,
  onTemplates,
}: {
  row: AdminLegalObligationRow;
  onEdit: () => void;
  onHistory: () => void;
  onTemplates: () => void;
}) {
  const missing = missingCatalogFields(row);
  // web 법정 서류 화면과 같은 한 자리 — 사본을 두지 않는다(그 함수 docblock)
  const recurrenceLabel = formatObligationRecurrence(row);

  return (
    <tr data-testid={`catalog-row-${row.key}`} className={row.isActive ? '' : 'bg-surface-soft'}>
      <td className="px-4 py-3 text-sm">
        <div className="font-semibold text-ink-900">{row.title}</div>
        <div className="mt-0.5 text-xs text-ink-500">{row.statuteCitation}</div>
      </td>
      <ValueCell label={recurrenceLabel} />
      <ValueCell
        label={row.periodBasis === null ? null : LEGAL_OBLIGATION_PERIOD_BASIS_LABEL[row.periodBasis]}
      />
      <ValueCell
        label={
          row.deadlineBasis === null ? null : LEGAL_OBLIGATION_DEADLINE_BASIS_LABEL[row.deadlineBasis]
        }
      />
      <ValueCell
        label={
          row.responsibleParty === null
            ? null
            : LEGAL_OBLIGATION_RESPONSIBLE_PARTY_LABEL[row.responsibleParty]
        }
      />
      <ValueCell label={LEGAL_OBLIGATION_AGENCY_FIELD_LABEL[row.agencyField]} />
      <td className="px-4 py-3 text-sm whitespace-nowrap">
        {row.isActive ? '활성' : <span className="text-ink-500">비활성</span>}
      </td>
      <td className="px-4 py-3 text-sm">
        <div className="flex flex-wrap gap-1">
          {blocksMaterialization(row) && (
            <Badge tone="rose" size="sm">
              이행 항목 안 만들어짐
            </Badge>
          )}
          {missing.length > 0 ? (
            <Badge tone="amber" size="sm">
              <span title={missing.map((f) => CATALOG_MISSING_FIELD_LABEL[f]).join(', ')}>
                미기재 칸 있음 ({missing.length})
              </span>
            </Badge>
          ) : (
            <Badge tone="emerald" size="sm">
              기재 완료
            </Badge>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">v{row.version}</td>
      <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
        <div className="inline-flex gap-2">
          <Button variant="secondary" size="sm" onClick={onHistory}>
            이력 {row.historyCount}
          </Button>
          <Button variant="secondary" size="sm" onClick={onTemplates}>
            양식
          </Button>
          <Button variant="primary" size="sm" onClick={onEdit}>
            편집
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ── 편집 ────────────────────────────────────────────────────────────────────

function CatalogEditModal({
  row,
  onClose,
  onSaved,
}: {
  row: AdminLegalObligationRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { showToast } = useToastStore();
  const initial = useMemo(() => rowToForm(row), [row]);
  const [form, setForm] = useState<CatalogFormState>(initial);
  const body = buildUpdateBody(row, form);
  const isDirty = body !== null || form.changeReason.trim() !== '';
  const blockedReason = formBlockedReason(form) ?? (body === null ? '바뀐 칸이 없습니다.' : undefined);

  const save = useMutation({
    mutationFn: () => {
      // blockedReason이 저장 버튼을 막으므로 여기 오는 body는 null이 아니다
      if (body === null) throw new Error('바뀐 칸이 없습니다.');
      return legalObligationCatalogApi.update(row.id, body);
    },
    onSuccess: (result) => {
      showToast(
        result.changedFields.length === 0
          ? '바뀐 칸이 없어 개정하지 않았습니다.'
          : `개정했습니다 — 이력 1건이 남고 v${row.version + 1}이 되었습니다.`,
        'success',
      );
      onSaved();
    },
    // ⚠️ 모달을 닫지 않는다 — 고친 입력을 잃지 않게(409 동시 개정이면 운영자가 다시 불러와야 한다)
    onError: (error) => showToast(extractApiError(error).message, 'error'),
  });

  const set = <K extends keyof CatalogFormState>(key: K, value: CatalogFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      title={`법정 의무 편집 — ${row.title}`}
      size="xl"
      isDirty={isDirty}
      closeLocked={save.isPending}
      footer={
        <EditFooter
          saving={save.isPending}
          blockedReason={blockedReason}
          onClose={onClose}
          onSave={() => save.mutate()}
        />
      }
    >
      <div className="space-y-5">
        <div
          data-testid="revision-notice"
          className="rounded-card border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          저장하면 <strong>개정 이력</strong>이 남고 버전이 v{row.version}에서 v{row.version + 1}로
          오릅니다. 이미 만들어진 현장의 이행 항목은 그때의 기준을 그대로 유지합니다.
        </div>

        <div className="text-xs text-ink-500">
          식별자 <code>{row.key}</code> · 표시 순서 {row.displayOrder} · 문자 알림{' '}
          {row.smsEscalation ? '포함' : '미포함'} · 조문 대조{' '}
          {row.lastVerifiedAt ? formatDateTimeISO(new Date(row.lastVerifiedAt)).slice(0, 10) : '안 됨'}
          <span className="ml-1">(이 넷은 이 화면에서 바꾸지 않습니다)</span>
        </div>

        <Field label="문서명">
          <input
            type="text"
            value={form.title}
            maxLength={300}
            onChange={(e) => set('title', e.target.value)}
            className={FORM_CONTROL_CLASS}
          />
        </Field>

        <Field label="설명">
          <textarea
            value={form.description}
            maxLength={5000}
            onChange={(e) => set('description', e.target.value)}
            className={FORM_TEXTAREA_CLASS}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="주기 종류" hint="비우면 현장에 이행 항목이 만들어지지 않습니다. 「상시」는 검토했더니 주기가 없는 상태 의무입니다.">
            <select
              value={form.recurrence}
              onChange={(e) => set('recurrence', e.target.value as CatalogFormState['recurrence'])}
              className={FORM_CONTROL_CLASS}
            >
              <option value="">미기재</option>
              {OBLIGATION_RECURRENCE_VALUES.map((value) => (
                <option key={value} value={value}>
                  {LEGAL_OBLIGATION_RECURRENCE_LABEL[value]}
                </option>
              ))}
            </select>
          </Field>

          <RecurrenceRuleFields form={form} set={set} />

          <Field label="기간 기준" hint="비우면 연도로 계산합니다. 건설 현장은 공사 기간을 고르세요.">
            <select
              value={form.periodBasis}
              onChange={(e) => set('periodBasis', e.target.value as CatalogFormState['periodBasis'])}
              className={FORM_CONTROL_CLASS}
            >
              <option value="">미기재</option>
              {Object.entries(LEGAL_OBLIGATION_PERIOD_BASIS_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="기한 기준" hint="비우면 기간이 끝나는 날을 기한으로 봅니다.">
            <select
              value={form.deadlineBasis}
              onChange={(e) =>
                set('deadlineBasis', e.target.value as CatalogFormState['deadlineBasis'])
              }
              className={FORM_CONTROL_CLASS}
            >
              <option value="">미기재</option>
              {Object.entries(LEGAL_OBLIGATION_DEADLINE_BASIS_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="담당 주체">
            <select
              value={form.responsibleParty}
              onChange={(e) =>
                set('responsibleParty', e.target.value as CatalogFormState['responsibleParty'])
              }
              className={FORM_CONTROL_CLASS}
            >
              <option value="">미기재</option>
              {Object.entries(LEGAL_OBLIGATION_RESPONSIBLE_PARTY_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="대행 분야"
            hint="어느 분야의 대행 기관이 이 서류를 작성하는지 고릅니다. 분야를 가리지 않는다면 「어느 분야든」을 고르세요."
          >
            <select
              value={form.agencyField}
              onChange={(e) => set('agencyField', e.target.value as CatalogFormState['agencyField'])}
              className={FORM_CONTROL_CLASS}
            >
              {Object.entries(LEGAL_OBLIGATION_AGENCY_FIELD_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="적용 범위" hint="「전 사업장」은 조문을 읽어 확인한 뒤에만 고르세요.">
            <select
              value={form.applicabilityScope}
              onChange={(e) =>
                set('applicabilityScope', e.target.value as CatalogFormState['applicabilityScope'])
              }
              className={FORM_CONTROL_CLASS}
            >
              {Object.entries(LEGAL_OBLIGATION_APPLICABILITY_SCOPE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="증빙 출처">
            <select
              value={form.evidenceSource}
              onChange={(e) =>
                set('evidenceSource', e.target.value as CatalogFormState['evidenceSource'])
              }
              className={FORM_CONTROL_CLASS}
            >
              {Object.entries(LEGAL_OBLIGATION_EVIDENCE_SOURCE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="법정 선임 직위" hint="직위 하나에 의무 하나만 둘 수 있습니다.">
            <select
              value={form.legalPosition}
              onChange={(e) =>
                set('legalPosition', e.target.value as CatalogFormState['legalPosition'])
              }
              className={FORM_CONTROL_CLASS}
            >
              <option value="">없음</option>
              {LEGAL_POSITION_ORDER.map((key) => (
                <option key={key} value={key}>
                  {LEGAL_POSITIONS[key].label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="근거 법령 인용" hint="원문 표기 그대로 적습니다(오기를 고쳐 적지 않습니다).">
            <input
              type="text"
              value={form.statuteCitation}
              onChange={(e) => set('statuteCitation', e.target.value)}
              className={FORM_CONTROL_CLASS}
            />
          </Field>
          <Field label="법령명">
            <input
              type="text"
              value={form.statuteLawName}
              maxLength={300}
              onChange={(e) => set('statuteLawName', e.target.value)}
              className={FORM_CONTROL_CLASS}
            />
          </Field>
          <Field label="조·항·호">
            <input
              type="text"
              value={form.statuteArticleRef}
              maxLength={200}
              onChange={(e) => set('statuteArticleRef', e.target.value)}
              className={FORM_CONTROL_CLASS}
            />
          </Field>
          {/* 체크박스는 자기 label을 가지므로 Field(<label>)로 감싸지 않는다 — label 중첩은 무효다 */}
          <div>
            <span className={FORM_LABEL_CLASS}>활성</span>
            <label className="inline-flex h-[42px] items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
              />
              활성 (끄면 새 이행 항목이 만들어지지 않습니다)
            </label>
          </div>
        </div>

        <Field label="출처 메모" hint="이 값을 어디서 확인했는지 적어 두세요.">
          <textarea
            value={form.sourceNote}
            maxLength={5000}
            onChange={(e) => set('sourceNote', e.target.value)}
            className={FORM_TEXTAREA_CLASS}
          />
        </Field>

        <ConditionsView conditions={row.conditions} />

        <IntervalVariantsView variants={row.intervalVariants} />

        <Field label="개정 사유" hint="이력에 함께 남습니다(법 개정 · 오기 정정 · 조문 검증 결과 등).">
          <textarea
            value={form.changeReason}
            maxLength={2000}
            onChange={(e) => set('changeReason', e.target.value)}
            className={FORM_TEXTAREA_CLASS}
          />
        </Field>
      </div>
    </Modal>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className={FORM_LABEL_CLASS}>{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  );
}

/**
 * 주기 규칙 칸(②-b · D1 ⓑ) — 종류에 따라 보이는 칸이 다르다: 「주기적」이면 반복 간격·기산(+ 「기준일부터」면 최초 간격·허용 창) ·
 * 「사유 발생 시」면 사건 기한 오프셋(D5). 조합의 모순은 `formBlockedReason`이 저장 전에 잡고(서버가 같은 함수로 한 번 더) 미리보기
 * 문구는 web과 같은 `formatObligationRecurrence`다.
 */
function RecurrenceRuleFields({
  form,
  set,
}: {
  form: CatalogFormState;
  set: <K extends keyof CatalogFormState>(key: K, value: CatalogFormState[K]) => void;
}) {
  const rule = formToRule(form);
  const unitOptions = (
    <>
      <option value="">—</option>
      {(Object.keys(LEGAL_OBLIGATION_INTERVAL_UNIT_LABEL) as Array<keyof typeof LEGAL_OBLIGATION_INTERVAL_UNIT_LABEL>).map((u) => (
        <option key={u} value={u}>
          {LEGAL_OBLIGATION_INTERVAL_UNIT_LABEL[u]}
        </option>
      ))}
    </>
  );
  if (form.recurrence === 'PERIODIC') {
    return (
      <>
        <Field label="반복 간격" hint={`미리보기: ${formatObligationRecurrence({ recurrence: 'PERIODIC', recurrenceRule: rule }) ?? ''}`}>
          <div className="flex gap-2">
            <input
              type="number"
              min={1}
              aria-label="반복 간격 수"
              value={form.intervalCount}
              onChange={(e) => set('intervalCount', e.target.value)}
              className={FORM_CONTROL_CLASS}
            />
            <select
              aria-label="반복 간격 단위"
              value={form.intervalUnit}
              onChange={(e) => set('intervalUnit', e.target.value as CatalogFormState['intervalUnit'])}
              className={FORM_CONTROL_CLASS}
            >
              {unitOptions}
            </select>
          </div>
        </Field>
        <Field label="기산" hint="달력 기준 = 달·분기·반기·연도 묶음(일·주 간격은 월 묶음) · 기준일부터 = 마지막 실시일(또는 설치·배치일)부터 간격.">
          <select
            value={form.intervalAnchor}
            onChange={(e) => set('intervalAnchor', e.target.value as CatalogFormState['intervalAnchor'])}
            className={FORM_CONTROL_CLASS}
          >
            <option value="">—</option>
            {(Object.keys(LEGAL_OBLIGATION_INTERVAL_ANCHOR_LABEL) as Array<keyof typeof LEGAL_OBLIGATION_INTERVAL_ANCHOR_LABEL>).map((a) => (
              <option key={a} value={a}>
                {LEGAL_OBLIGATION_INTERVAL_ANCHOR_LABEL[a]}
              </option>
            ))}
          </select>
        </Field>
        {form.intervalAnchor === 'ROLLING' && (
          <>
            <Field label="최초 간격" hint="설치·신설·배치일부터 첫 기한까지(비우면 반복 간격과 같다). 안전검사 「최초 3년」·근골격계 「신설 1년」.">
              <div className="flex gap-2">
                <input
                  type="number"
                  min={1}
                  aria-label="최초 간격 수"
                  value={form.initialIntervalCount}
                  onChange={(e) => set('initialIntervalCount', e.target.value)}
                  className={FORM_CONTROL_CLASS}
                />
                <select
                  aria-label="최초 간격 단위"
                  value={form.initialIntervalUnit}
                  onChange={(e) => set('initialIntervalUnit', e.target.value as CatalogFormState['initialIntervalUnit'])}
                  className={FORM_CONTROL_CLASS}
                >
                  {unitOptions}
                </select>
              </div>
            </Field>
            <Field label="허용 창(개월)" hint="「매 N년이 되는 날을 기준으로 전후 M개월」의 M. 비우면 기한 당일까지.">
              <input
                type="number"
                min={1}
                value={form.cycleWindowMonths}
                onChange={(e) => set('cycleWindowMonths', e.target.value)}
                className={FORM_CONTROL_CLASS}
              />
            </Field>
          </>
        )}
      </>
    );
  }
  if (form.recurrence === 'ON_EVENT') {
    return (
      <Field label="사건 기한" hint="사건일부터 법정 기한까지(0 = 사건 당일). 비우면 고정 기한 없음 — 현장이 사건마다 기한을 직접 넣습니다.">
        <div className="flex gap-2">
          <input
            type="number"
            min={0}
            aria-label="사건 기한 수"
            value={form.eventDeadlineCount}
            onChange={(e) => set('eventDeadlineCount', e.target.value)}
            className={FORM_CONTROL_CLASS}
          />
          <select
            aria-label="사건 기한 단위"
            value={form.eventDeadlineUnit}
            onChange={(e) => set('eventDeadlineUnit', e.target.value as CatalogFormState['eventDeadlineUnit'])}
            className={FORM_CONTROL_CLASS}
          >
            {unitOptions}
          </select>
        </div>
      </Field>
    );
  }
  return null;
}

/** 간격 변형 줄 — **보기 전용**(조건 줄과 같다 · 값은 마이그레이션이 넣는다) */
function IntervalVariantsView({ variants }: { variants: AdminLegalObligationRow['intervalVariants'] }) {
  if (variants.length === 0) return null;
  const target: Record<AdminLegalObligationRow['intervalVariants'][number]['target'], string> = {
    PERIOD: '반복 간격',
    INITIAL: '최초 간격',
    EVENT_DEADLINE: '사건 기한',
  };
  return (
    <div data-testid="interval-variants-view">
      <div className={FORM_LABEL_CLASS}>간격 변형 (보기 전용)</div>
      <ul className="space-y-1 text-sm text-ink-700">
        {variants.map((v) => (
          <li key={v.id}>
            {target[v.target]} → {formatObligationInterval(v.interval)}
            {v.anchor === 'ROLLING' ? '(실시일부터)' : v.anchor === 'CALENDAR' ? '(달력)' : ''}
            {v.selector === 'SITE_FACT'
              ? ` — 현장 ${v.axis === 'INDUSTRY' ? '업종' : v.axis ?? ''} ${v.valueKeys.join(', ')}`
              : v.selector === 'RESULT_FACT'
                ? ` — 측정·진단 결과 ${v.valueKeys.join(', ')}${v.oneCycleOnly ? ' (다음 1회 한정)' : ''}`
                : ` — 등록 시 선택: ${v.label ?? ''}`}
          </li>
        ))}
      </ul>
      <p className="mt-1 text-xs text-ink-500">현장 업종이 없거나 변형보다 얕으면 가장 짧은 간격을 적용하고 「확인 필요」로 표시합니다.</p>
    </div>
  );
}

/** 적용 조건 — **보기 전용**(서버에 쓰기 입구가 없다 · 페이지 docblock) */
function ConditionsView({ conditions }: { conditions: AdminLegalObligationConditionView[] }) {
  return (
    <div data-testid="conditions-view">
      <div className={FORM_LABEL_CLASS}>적용 조건 (보기 전용)</div>
      {conditions.length === 0 ? (
        <p className="text-sm text-ink-500">
          등록된 조건이 없습니다. 조건이 없다고 「전 사업장 적용」이라는 뜻은 아닙니다 — 적용 범위 칸을
          보세요.
        </p>
      ) : (
        <ul className="space-y-1 text-sm text-ink-700">
          {conditions.map((c) => (
            <li key={c.id}>
              {LEGAL_OBLIGATION_CONDITION_AXIS_LABEL[c.axis]}: {describeCondition(c)}
              {c.variesByIndustry && (
                <span className="text-ink-500"> (업종에 따라 기준이 다름 — 미달이어도 제외하지 않고 「확인 필요」로 둡니다)</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-xs text-ink-500">조건은 이 화면에서 바꿀 수 없습니다.</p>
    </div>
  );
}

function describeCondition(c: AdminLegalObligationConditionView): string {
  if (c.valueKeys.length > 0) return c.valueKeys.join(', ');
  const unit = c.unit ?? '';
  const parts: string[] = [];
  if (c.minValue !== null) parts.push(`${c.minValue}${unit} ${c.minInclusive ? '이상' : '초과'}`);
  if (c.maxValue !== null) parts.push(`${c.maxValue}${unit} ${c.maxInclusive ? '이하' : '미만'}`);
  return parts.length > 0 ? parts.join(' · ') : '값 미정';
}

/**
 * 푸터는 별도 컴포넌트여야 한다 — `useModalRequestClose`는 `Modal` children 하위에서만 값을 잡는다
 * (선례 `Announcements`의 `FormFooter`). 취소는 `requestClose`를 거쳐 dirty 확인을 받는다.
 */
function EditFooter({
  saving,
  blockedReason,
  onClose,
  onSave,
}: {
  saving: boolean;
  /** 저장을 막는 사유 — 있으면 곧 비활성이다(별 플래그를 두지 않는다 · 선례 `FormFooter`) */
  blockedReason?: string;
  onClose: () => void;
  onSave: () => void;
}) {
  const requestClose = useModalRequestClose();
  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => (requestClose ?? onClose)()}
        disabled={saving}
      >
        취소
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={onSave}
        disabled={saving || blockedReason !== undefined}
        title={blockedReason}
      >
        {saving ? '저장 중...' : '저장 (개정 이력 남김)'}
      </Button>
    </>
  );
}

// ── 이력 ────────────────────────────────────────────────────────────────────

function CatalogHistoryModal({
  row,
  onClose,
}: {
  row: AdminLegalObligationRow;
  onClose: () => void;
}) {
  const history = useQuery({
    queryKey: LEGAL_OBLIGATION_CATALOG_QUERY_KEYS.history(row.id),
    queryFn: () => legalObligationCatalogApi.history(row.id),
  });
  const items = history.data ?? [];

  return (
    <Modal open onClose={onClose} title={`개정 이력 — ${row.title}`} size="xl">
      <p className="mb-3 text-sm text-ink-600">
        현재 v{row.version}. 각 행의 버전은 <strong>그 개정 «전»</strong> 내용의 버전입니다.
      </p>
      {history.isLoading ? (
        <div className="py-6 text-center text-gray-500">불러오는 중...</div>
      ) : history.error ? (
        <div className="p-3 bg-red-50 border border-red-200 rounded-card text-sm text-red-700">
          이력을 불러오지 못했습니다. {extractApiError(history.error).message}
        </div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-gray-500">아직 개정된 적이 없습니다.</div>
      ) : (
        <ol className="space-y-3">
          {items.map((h) => (
            <li key={h.id} className="rounded-card border border-line p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-ink-900">
                  v{h.version} → v{h.version + 1}
                </span>
                <span className="text-xs text-ink-500">
                  {formatDateTimeISO(new Date(h.changedAt)).replace('T', ' ')}
                </span>
              </div>
              <div className="mt-1 text-ink-700">
                바뀐 칸: {h.changedFields.map(revisionFieldLabel).join(', ') || '없음'}
              </div>
              {h.changeReason && <div className="mt-1 text-ink-600">사유: {h.changeReason}</div>}
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
